/**
 * KpiStrip — three-up KPI card used at the top of each Posture page.
 *
 * Each KPI is composed of:
 *   - large italic dropcap numeral (tone-tinted by default; can be overridden
 *     per-KPI to flag a single warn/crit cell — matches the mockup pattern)
 *   - uppercase tracked label
 *   - one-sentence stat ("Awaiting your decision")
 *   - optional trend line ("−3 vs. last week · 9d trend down")
 *   - optional sparkline node (recharts <ResponsiveContainer> handed in by
 *     the page so the data-fetching stays page-side)
 *
 * Visual reference: `.kpi-strip` block in 01-baseline-compliance.html and
 * 03-user-analytics.html. Three columns on lg+, single column stacked
 * on smaller viewports.
 */

import clsx from 'clsx'
import type { PillarTone } from '@/components/Dashboard/v2/PillarShell'

/** Severity hint for a single KPI. `tone` (default) uses the pillar accent;
 *  `warn` / `crit` switch to sand / red so the page can call out a single
 *  inflamed metric without restating the whole tone. */
export type KpiSeverity = 'tone' | 'warn' | 'crit'

export interface KpiCell {
  /** Stable key per-KPI; helps React diffing if the page swaps cells. */
  id: string
  /** Big italic dropcap numeral. Pre-formatted by the page so we don't
   *  re-inject a number formatter (locale + zero-pad rules vary). */
  numeral: string
  /** Optional secondary symbol next to the numeral (e.g. "%"). */
  numeralSuffix?: string
  /** Uppercase tracked label, e.g. "Active drifts". */
  label: string
  /** One-sentence stat below the label. Plain prose, kept short. */
  stat: string
  /** Optional trend line — bolds inside this string render via `**bold**`. */
  trend?: string
  /** Optional sparkline node, typically a recharts <ResponsiveContainer />.
   *  The page owns the data; we just give it a sized slot. */
  spark?: React.ReactNode
  /** Severity tint for the numeral. Defaults to the pillar tone. */
  severity?: KpiSeverity
}

interface Props {
  /** The pillar tone — drives the default numeral color. */
  tone: PillarTone
  /** Exactly three cells for the three-up layout. The component does not
   *  enforce three at runtime but the visual slot expects it. */
  cells: ReadonlyArray<KpiCell>
}

/** Render `**bold**` markers in trend copy as <strong>. Same shorthand the
 *  brief composers use, so the page can build a single string and trust
 *  the bold to land. */
function renderTrend(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong
          key={i}
          className="font-bold"
          style={{ color: 'var(--tone-accent-deep)' }}
        >
          {part.slice(2, -2)}
        </strong>
      )
    }
    return <span key={i}>{part}</span>
  })
}

/** Color expression for the dropcap numeral by severity. The default
 *  ("tone") routes through the `[data-tone]` accent map so per-pillar
 *  tinting flows from a single source.
 *
 *  Warn and crit are pinned hex literals for the same reason `#212938` is
 *  pinned in the mockups: they're editorial accents, not surface tokens —
 *  they should NOT swap with theme. */
const NUMERAL_COLOR: Record<KpiSeverity, string> = {
  tone: 'var(--tone-accent-deep)',
  warn: 'rgb(var(--color-secondary-sand-500))',
  crit: '#C84B4B',
}

export default function KpiStrip({ tone, cells }: Props) {
  return (
    <section
      data-tone={tone}
      className="
        mb-9 grid grid-cols-1 overflow-hidden rounded-3xl border border-dark-600
        bg-dark-800 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]
        md:grid-cols-3
      "
    >
      {cells.map((cell, idx) => {
        const severity = cell.severity ?? 'tone'
        return (
          <div
            key={cell.id}
            className={clsx(
              'flex items-center gap-5 px-7 py-7',
              // Vertical column dividers between the three cells. Drop the
              // right border on the last cell, and switch to bottom-borders
              // on small viewports where the layout collapses to one column.
              idx < cells.length - 1 &&
                'border-b border-dark-700 md:border-b-0 md:border-r md:border-dark-700',
            )}
          >
            <div
              className="font-display font-extrabold italic"
              style={{
                fontSize: 'clamp(40px, 4.6vw, 60px)',
                lineHeight: 0.9,
                letterSpacing: '-0.04em',
                color: NUMERAL_COLOR[severity],
              }}
            >
              {cell.numeral}
              {cell.numeralSuffix && (
                <span
                  className="ml-1 text-[22px] font-semibold"
                  style={{ color: 'rgb(var(--color-text-secondary))' }}
                >
                  {cell.numeralSuffix}
                </span>
              )}
            </div>

            <div className="flex flex-1 flex-col gap-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                {cell.label}
              </div>
              <div className="text-[15px] font-bold leading-tight text-slate-100 dark:text-slate-100">
                {cell.stat}
              </div>
              {cell.trend && (
                <div className="text-[11px] leading-[1.4] text-slate-400">
                  {renderTrend(cell.trend)}
                </div>
              )}
            </div>

            {cell.spark && (
              <div
                aria-hidden
                className="ml-auto h-9 w-[90px] flex-shrink-0"
              >
                {cell.spark}
              </div>
            )}
          </div>
        )
      })}
    </section>
  )
}
